import React, { useState, useEffect, lazy, Suspense, useMemo } from 'react'
import { useMutation } from '@apollo/client'
import { useSelector, useDispatch } from 'react-redux'
import { Route, Redirect, Switch, useLocation, useHistory } from 'react-router-dom'
import { Footer } from './Components/NavBar'
import { SIGNIN_BY_TOKEN } from './query/userQuery'
import { logout, onLogin, setAgeGate } from './redux/Dashboard/UserReducer'
import { AUTH_TOKEN, SUCCESS, TEACHER, PARENT, SIGN_IN_STATE } from './constants'
import { BLACK } from './colors'
import { CookieNotice } from './Components/Dashboard'
import PrivateRoute from './Components/PrivateRoute'
import i18n, { options } from './i18n'
import Spinner from './Components/Spinner'
import { theme } from './theme.js'
import './App.css'
import MainNavbar from './Components/Home/MainNavbar.jsx'
import useDeviceDetection from './Components/hooks/useDeviceDetection.js'
import MobileNotification from './Components/Meerkat/Dashboard/MobileNotification.jsx'
import MetaTagsComponent from './Components/Meerkat/Lessons/MetaTagComponent.jsx'
import LessonsAndResources from './Components/Mobile/Components/LessonAndResources.jsx'
import MobileHeader from './Components/Mobile/Components/Header.jsx'
import MobileLessonDetail from './Components/Mobile/Pages/MobileLessonDetail.jsx'
import { onLoadMainNavbar } from './redux/Dashboard/PageReducer.js'
import SgcMobilePage from './Components/SGC/Pages/SgcMobilePage.jsx'
import { useVersionCheck } from './Components/hooks/useVersionCheck.js'

const SignInPage = lazy(() => import('./Components/Meerkat/Users/SignIn/SignInPage'))
const TokenSignIn = lazy(() => import('./Components/Meerkat/Users/SignIn/TokenSignIn'))
const SignUpPage = lazy(() => import('./Components/Meerkat/Users/SignUp/SignUpPage'))
const Expired = lazy(() => import('./Components/Meerkat/Users/SessionExpired'))
const GoogleSyncLoginPage = lazy(() => import('./Components/Meerkat/Users/GoogleSyncLoginPage'))
const CleverLoginPage = lazy(() => import('./Components/Meerkat/Users/CleverLoginPage'))
const CleverPortalPage = lazy(() => import('./Components/Meerkat/Users/SignIn/CleverPortalPage'))
const ForgotPassword = lazy(() => import('./Components/Meerkat/Users/SignIn/ForgotPassword'))
const ResetPassword = lazy(() => import('./Components/Meerkat/Users/SignIn/ResetPassword'))
const ConfirmPassword = lazy(() => import('./Components/Meerkat/Users/SignIn/ConfirmPassword'))
const ProfilePage = lazy(() => import('./Components/Meerkat/Dashboard/ProfilePage'))
const LandingPage = lazy(() => import('./Components/Meerkat/LandingPage'))
const EduHomePage = lazy(() => import('./Components/Meerkat/Sphero/Home.jsx'))
const RemixesPage = lazy(() => import('./Components/Meerkat/Programs/RemixesPage'))
const EduActivitiesPage = lazy(() => import('./Components/Meerkat/Sphero/ActivitiesPage.jsx'))
const EduResourcesPage = lazy(() => import('./Components/Meerkat/Sphero/Resources.jsx'))
const EduMyContentPage = lazy(() => import('./Components/Meerkat/Sphero/Components/MyContent/MyContentPage.jsx'))
const BlueprintHomePage = lazy(() => import('./Components/Meerkat/Blueprint/Home.jsx'))
const BlueprintLessonsPage = lazy(() => import('./Components/Meerkat/Blueprint/LessonsPage.jsx'))
const BlueprintResourcesPage = lazy(() => import('./Components/Meerkat/Blueprint/Resources.jsx'))
const LittlebitsHomePage = lazy(() => import('./Components/Meerkat/Littlebits/Home.jsx'))
const LittlebitsLessonsPage = lazy(() => import('./Components/Meerkat/Littlebits/LessonsPage.jsx'))
const LittlebitsResourcesPage = lazy(() => import('./Components/Meerkat/Littlebits/Resources.jsx'))
const IndiHomePage = lazy(() => import('./Components/Meerkat/Indi/Home.jsx'))
const IndiLessonsPage = lazy(() => import('./Components/Meerkat/Indi/LessonsPage.jsx'))
const IndiResourcesPage = lazy(() => import('./Components/Meerkat/Indi/Resources.jsx'))
const SpheroClassroomPage = lazy(() => import('./Components/Meerkat/Sphero/Classrooms/ClassroomManagementPage'))
const PrintClassCodePage = lazy(() => import('./Components/Meerkat/Sphero/Classrooms/PrintClassCodePage'))
const SpheroFamilyPage = lazy(() => import('./Components/Meerkat/Sphero/Families/FamilyManagementPage.jsx'))
const CollectionDetail = lazy(() => import('./Components/Meerkat/Lessons/CollectionDetail.jsx'))
const LessonDetail = lazy(() => import('./Components/Meerkat/Lessons/LessonDetail'))
const SpheroActivityDetail = lazy(() => import('./Components/Meerkat/Activities/Details/ActivityDetailsPage'))
const SpheroAssignmentDetail = lazy(() => import('./Components/Meerkat/Activities/Details/AssignmentDetailPage'))
const ActivityBuilder = lazy(() => import('./Components/Meerkat/Activities/Builder/Builder'))
const CodeEditor = lazy(() => import('./Components/Programs/CodeEditor'))
const ProgramsPage = lazy(() => import('./Components/Meerkat/Programs/ProgramsPage'))
const SplitViewPage = lazy(() => import('./Components/Meerkat/Activities/Details/SplitViewPage'))
const ProgramDetail = lazy(() => import('./Components/Meerkat/Programs/ProgramDetails/ProgramDetailPage.jsx'))
const DownloadPage = lazy(() => import('./Components/Meerkat/Dashboard/DownloadApp'))
const NewTeacherPage = lazy(() => import('./Components/Meerkat/Dashboard/NewTeacherInf'))
const AccessibilityPage = lazy(() => import('./Components/Meerkat/Dashboard/AccessibilityPage'))
const PrivacyPolicy = lazy(() => import('./Components/Meerkat/Privacy/PrivacyPolicy'))
const NotFoundPage = lazy(() => import('./Components/NotFoundPage'))
const ActivityModeration = lazy(() => import('./Components/AdminTools/ActivityModerationPage'))

// Here is Sphero Global Challenge
const SgcLandingPage = lazy(() => import('./Components/SGC/Pages/Landing.jsx'))
const RedemptionPage = lazy(() => import('./Components/SGC/Pages/Redemption.jsx'))
const CompetitionEntryPage = lazy(() => import('./Components/SGC/Pages/CompetitionEntry.jsx'))
// const SgcMobilePage = lazy(() => import('./Components/SGC/Pages/SgcMobilePage.jsx'))
const SgcResourcesPage = lazy(() => import('./Components/SGC/Pages/Resources.jsx'))

// Here is Mobile Page
const MobileLandingPage = lazy(() => import('./Components/Mobile/Pages/MobileLanding.jsx'))

export const App = () => {
  // eslint-disable-next-line no-undef
  global.appVersion = process.env.REACT_APP_VERSION
  // Here is a Meerkat

  useVersionCheck()

  const [hasInvalidToken, setInvalidToken] = useState(false)
  const [translationsLoaded, setTranslationsLoaded] = useState(false)
  const [showMainNavbar, setShowMainNavbar] = useState(false)
  const isMobileDevice = useDeviceDetection()

  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()

  const { isLoggedIn, user, classType, admin } = useSelector((state) => state.authentication)

  useEffect(() => {
    dispatch(onLoadMainNavbar({ mainNavbar: !isMobileDevice }))
  }, [isMobileDevice])

  useEffect(() => {
    window.dataLayer.push({
      event: 'pageview',
      page: {
        url: window.location.origin + window.location.pathname,
        title: window.location.pathname
      }
    })
  }, [window.location.pathname])

  const [signInByToken] = useMutation(SIGNIN_BY_TOKEN, {
    onError (err) {
      console.error(err)
    },
    onCompleted (payload) {
      const { token, user, status, errors } = payload.signInByToken
      if (status === SUCCESS) {
        dispatch(onLogin({ token, user }))
      } else {
        setInvalidToken(true)
        console.error(errors)
      }
    }
  })

  // prevents delete/backspace key from navigation back
  window.onkeydown = function (event) {
    if (
      event.which === 8 &&
      event.target.tagName !== 'INPUT' &&
      event.target.type !== 'text' &&
      event.target.type !== 'password' &&
      event.target.type !== 'textarea' &&
      event.target.classList[0] !== 'ql-editor'
    ) {
      event.preventDefault()
    }
  }

  useEffect(() => {
    fetch('https://agegator.platform.sphero.com/')
      .then((res) => res.json())
      .then((data) => {
        dispatch(setAgeGate({ ageGate: data.AgeGate }))
      })
  }, [])

  useEffect(() => {
    window.addEventListener('storage', (e) => {
      if (e.key === AUTH_TOKEN && window.localStorage.getItem('auth-token') === '' && isLoggedIn) {
        dispatch(logout())
      }
    })
  }, [window.localStorage.getItem(AUTH_TOKEN), isLoggedIn])

  useEffect(() => {
    const { pathname, search } = location

    if (pathname.startsWith('/signup') || pathname.startsWith('/signin')) {
      setShowMainNavbar(true)
    } else {
      setShowMainNavbar(false)
    }
    if (window.location.href.includes('/cwists/?active=1')) {
      history.push('/edurobots/collections?page=1')
    }
    if (window.location.href.includes('/cwists/?active=2')) {
      history.push('/edurobots/collections?page=2')
    }
    if (pathname.startsWith('/premium_content')) {
      history.push('/edurobots/collections?page=3')
    }
    if (search.indexOf('token') !== -1) {
      const parts = search.split('=')
      const token = parts[1].slice(0, parts[1].indexOf('&'))
      signInByToken({ variables: { token } })
    }
  }, [location, classType])

  const token = useMemo(() => window.localStorage.getItem(AUTH_TOKEN), [window.localStorage])

  useEffect(() => {
    if (token && isLoggedIn === false && hasInvalidToken !== true) {
      signInByToken({ variables: { token: window.localStorage.getItem(AUTH_TOKEN) } })
    }
  }, [isLoggedIn])

  useEffect(() => {
    if (!i18n.isInitialized) {
      i18n.init(options, () => setTranslationsLoaded(true))
    }
  }, [i18n.isInitialized])

  if (!translationsLoaded) {
    return (
      <div
        style={{
          padding: '0',
          backgroundColor: BLACK,
          width: '100%',
          minHeight: '100vh'
        }}
      ><Spinner />
      </div>
    )
  }

  if (isMobileDevice) {
    return (
      <div
        className='App'
        style={{ display: 'flex', flexDirection: 'column', fontFamily: 'Montserrat' }}
      >
        <MobileHeader />
        <Suspense fallback={<Spinner />}>
          <Switch>
            <Route exact path='/landing' component={MobileLandingPage} />

            <Route exact path='/signin' component={SignInPage} />
            <Route exact path='/signin/:token' component={TokenSignIn} />
            <Route exact path={`/${SIGN_IN_STATE.EXPIRED}`} component={Expired} />
            <Route exact path='/signup/:signInParam' component={SignUpPage} />
            <Route exact path='/account/class-code' component={SignInPage} />
            <Route exact path='/account/signup' component={SignInPage} />
            <Route exact path='/google/callback/' component={GoogleSyncLoginPage} />
            <Route exact path='/clever/callback/*' component={CleverLoginPage} />
            <Route exact path='/clever_portal/:token' component={CleverPortalPage} />
            <Route exact path='/forgot_password' component={ForgotPassword} />
            <Route exact path='/account/forgot_password' render={() => <Redirect to='/forgot_password' />} />
            <Route exact path='/account/reset_password/:token' component={ResetPassword} />
            <Route exact path='/password/:token/confirm' component={ConfirmPassword} />
            <Route path='/user_settings' render={() => <Redirect to='/profile/settings' />} />
            <Route exact path='/child-policy' component={PrivacyPolicy} />
            <PrivateRoute
              exact
              path='/profile/:opentab'
              isAuthenticated={isLoggedIn}
              isAuthorized
              render={() => (
                <Suspense fallback={<div />}>
                  <ProfilePage />
                </Suspense>
              )}
            />
            <PrivateRoute
              exact
              path='/edurobots/classrooms'
              isAuthenticated={isLoggedIn}
              isAuthorized={user?.persona === TEACHER}
              render={() => (
                <Suspense fallback={<div />}>
                  <SpheroClassroomPage />
                </Suspense>
              )}
            />
            <PrivateRoute
              exact
              path='/edurobots/my_content/:opentab'
              isAuthenticated={isLoggedIn}
              isAuthorized
              render={({
                match: {
                  params: { opentab }
                }
              }) => <EduMyContentPage opentab={opentab} />}
            />
            <PrivateRoute
              exact
              path='/edurobots/family'
              isAuthenticated={isLoggedIn}
              isAuthorized={user?.persona === PARENT}
              render={() => (
                <Suspense fallback={<div />}>
                  <SpheroFamilyPage />
                </Suspense>
              )}
            />

            <Route exact path='/cwists/preview/:slug' component={() => <MobileLessonDetail product='edurobots' />} />
            <Route exact path='/' render={() => <Redirect to='/landing' />} />
            <Route exact path='/sphero/home' render={() => <Redirect to='/landing' />} />
            <Route exact path='/d' render={() => { window.location = `${window.location.origin}/downloads` }} />
            <Route exact path='/downloads' render={() => <DownloadPage showModal />} />
            <Route exact path='/sgc' render={() => <SgcMobilePage />} />
            <Route exact path='/sgc/*' render={() => <SgcMobilePage />} />
            <Route exact path='/:robot' component={LessonsAndResources} />
            <Route exact path='/lessons/:slug' component={MobileLessonDetail} />
            <Route exact path='/:robot/:type' component={LessonsAndResources} />
            <Route exact path='/family' render={() => <Redirect to='/edurobots/family' />} />
          </Switch>
        </Suspense>
      </div>
    )
  }

  return (
    <>
      {isMobileDevice && <MobileNotification />}
      <div
        className='App'
        style={{ display: 'flex', flexDirection: 'column', fontFamily: 'Montserrat' }}
      >
        <>
          <div
            style={{
              padding: '0',
              backgroundColor: BLACK,
              width: '100%',
              minHeight: '100vh'
            }}
          >
            <div
              style={{
                display: 'flex',
                width: '100%',
                flexDirection: 'row',
                height: '100%',
                minHeight: '100vh'
              }}
            >
              <div
                style={{
                  padding: 0,
                  width: '100%',
                  flex: '1 1 auto',
                  alignSelf: 'stretch',
                  backgroundColor: theme.dark.background,
                  color: theme.dark.fontColor
                }}
              >
                {showMainNavbar && <MainNavbar />}
                <Suspense fallback={<Spinner />}>
                  <Switch>
                    <Route exact path='/landing' component={LandingPage} />
                    <Route exact path='/edurobots' component={EduHomePage} />
                    <Route exact path='/edurobots/collections' component={EduActivitiesPage} />
                    <Route exact path='/edurobots/resources' component={EduResourcesPage} />
                    <Route exact path='/blueprint' component={BlueprintHomePage} />
                    <Route exact path='/blueprint/collections' component={BlueprintLessonsPage} />
                    <Route exact path='/blueprint/resources' component={BlueprintResourcesPage} />
                    <Route exact path='/littlebits' component={LittlebitsHomePage} />
                    <Route exact path='/littlebits/collections' component={LittlebitsLessonsPage} />
                    <Route
                      exact
                      path='/littlebits/resources' render={() => (
                        <>
                          <MetaTagsComponent
                            title='Sphero Central littleBits Resources for Teachers'
                            description='Explore our library of littleBits resources for educators in Sphero Central. Find lesson plans, teacher guides, and project ideas to inspire hands-on learning in STEM education!'
                          />
                          <LittlebitsResourcesPage />
                        </>
                      )}
                    />
                    <Route exact path='/indi' component={IndiHomePage} />
                    <Route exact path='/indi/collections' component={IndiLessonsPage} />
                    <Route exact path='/indi/resources' component={IndiResourcesPage} />
                    <Route exact path='/sgc' component={SgcLandingPage} />
                    <Route exact path='/signin' component={SignInPage} />
                    <Route exact path='/signin/:token' component={TokenSignIn} />
                    <Route exact path={`/${SIGN_IN_STATE.EXPIRED}`} component={Expired} />
                    <Route exact path='/signup/:signInParam' component={SignUpPage} />
                    <Route exact path='/account/class-code' component={SignInPage} />
                    <Route exact path='/account/signup' component={SignInPage} />
                    <Route exact path='/google/callback/' component={GoogleSyncLoginPage} />
                    <Route exact path='/clever/callback/*' component={CleverLoginPage} />
                    <Route exact path='/clever_portal/:token' component={CleverPortalPage} />
                    <Route exact path='/forgot_password' component={ForgotPassword} />
                    <Route exact path='/account/forgot_password' render={() => <Redirect to='/forgot_password' />} />
                    <Route exact path='/account/reset_password/:token' component={ResetPassword} />
                    <Route exact path='/password/:token/confirm' component={ConfirmPassword} />
                    <Route exact path='/collections/:id' component={CollectionDetail} />
                    <Route exact path='/lessons/:slug' component={LessonDetail} />
                    <Route exact path='/cwists/preview/:slug' component={SpheroActivityDetail} />
                    <Route exact path='/assignment/:slug' component={SpheroAssignmentDetail} />
                    <Route exact path='/activities/:slug/edit/:index' component={ActivityBuilder} />
                    <Route exact path='/activity/program-view/:slug' component={SplitViewPage} />
                    <Route exact path='/assignment/program-view/:slug' component={SplitViewPage} />
                    <Route exact path='/program/:id' component={ProgramDetail} />
                    <Route exact path='/program/:id/edit' component={CodeEditor} />
                    <Route exact path='/remixes/:id' component={RemixesPage} />
                    <Route exact path='/programs/' component={ProgramsPage} />
                    <Route exact path='/programs/:filterparams' component={ProgramsPage} />
                    <Route exact path='/d' render={() => { window.location = `${window.location.origin}/downloads` }} />
                    <Route exact path='/downloads' render={() => <DownloadPage showModal />} />
                    <Route exact path='/activate/confirmation_token/:token' component={TokenSignIn} />
                    <Route exact path='/new_teacher' component={NewTeacherPage} />
                    <Route exact path='/child-policy' component={PrivacyPolicy} />
                    <Route exact path='/accessibility' component={AccessibilityPage} />

                    <Route exact path='/remixes' render={() => <Redirect to='/programs/' />} />
                    <Route path='/user_settings' render={() => <Redirect to='/profile/settings' />} />
                    <Route exact path='/paid_content' render={() => <Redirect to='/edurobots/collections?page=3' />} />
                    <Route exact path='/' render={() => <Redirect to='/landing' />} />
                    <Route exact path='/sphero/home' render={() => <Redirect to='/landing' />} />
                    <Route exact path='/cwists' render={() => <Redirect to='/edurobots/collections' />} />
                    <Route exact path='/my_content/programs' render={() => <Redirect to='/edurobots/my_content/programs' />} />
                    <Route exact path='/my_content/activities' render={() => <Redirect to='/edurobots/my_content/activities' />} />
                    <Route exact path='/my_content/workbook' render={() => <Redirect to='/edurobots/my_content/workbook' />} />
                    <Route exact path='/classrooms' render={() => <Redirect to='/edurobots/classrooms' />} />
                    <Route exact path='/gettingstarted' render={() => <Redirect to='/landing' />} />
                    <Route exact path='/sgc/resources' component={SgcResourcesPage} />
                    <PrivateRoute
                      exact
                      path='/profile/:opentab'
                      isAuthenticated={isLoggedIn}
                      isAuthorized
                      render={() => (
                        <Suspense fallback={<div />}>
                          <ProfilePage />
                        </Suspense>
                      )}
                    />
                    <PrivateRoute
                      exact
                      path='/sgc/redeem'
                      isAuthenticated={isLoggedIn}
                      isAuthorized={user?.persona === PARENT || user?.persona === TEACHER}
                      render={() => (
                        <Suspense fallback={<div />}>
                          <RedemptionPage />
                        </Suspense>
                      )}
                    />
                    <PrivateRoute
                      exact
                      path='/sgc/entry/:id'
                      isAuthenticated={isLoggedIn}
                      isAuthorized={user?.persona === PARENT || user?.persona === TEACHER}
                      render={() => (
                        <Suspense fallback={<div />}>
                          <CompetitionEntryPage />
                        </Suspense>
                      )}
                    />
                    <PrivateRoute
                      exact
                      path='/edurobots/my_content/:opentab'
                      isAuthenticated={isLoggedIn}
                      isAuthorized
                      render={({
                        match: {
                          params: { opentab }
                        }
                      }) => <EduMyContentPage opentab={opentab} />}
                    />
                    <PrivateRoute
                      exact
                      path='/edurobots/classrooms'
                      isAuthenticated={isLoggedIn}
                      isAuthorized={user?.persona === TEACHER}
                      render={() => (
                        <Suspense fallback={<div />}>
                          <SpheroClassroomPage />
                        </Suspense>
                      )}
                    />
                    <PrivateRoute
                      exact
                      path='/edurobots/family'
                      isAuthenticated={isLoggedIn}
                      isAuthorized={user?.persona === PARENT}
                      render={() => (
                        <Suspense fallback={<div />}>
                          <SpheroFamilyPage />
                        </Suspense>
                      )}
                    />
                    <PrivateRoute
                      exact
                      path='/moderation'
                      isAuthenticated={isLoggedIn}
                      isAuthorized={admin}
                      render={() => (
                        <Suspense fallback={<div />}>
                          <ActivityModeration />
                        </Suspense>
                      )}
                    />
                    <PrivateRoute
                      path='/moderation/activity/:slug'
                      isAuthenticated={isLoggedIn}
                      isAuthorized={admin}
                      render={({
                        match: {
                          params: { slug }
                        }
                      }) => <SpheroActivityDetail slug={slug} />}
                    />
                    <PrivateRoute
                      path='/print'
                      isAuthenticated={isLoggedIn}
                      isAuthorized={user?.persona === TEACHER}
                      render={() => (
                        <Suspense fallback={<div />}>
                          <PrintClassCodePage />
                        </Suspense>
                      )}
                    />
                    <Route path='*' component={NotFoundPage} />
                  </Switch>
                </Suspense>
              </div>
            </div>
          </div>
          <CookieNotice />
          <Footer />
        </>
      </div>
    </>
  )
}

export default App
